import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Stack, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import OtpInput from 'react-otp-input';
import { api } from 'src/Api';
import { postJson } from 'src/utils/clutchApi';
import { RESET_EMAIL_KEY, RESET_TOKEN_KEY } from 'src/utils/clutchAdminAuth';

export default function OtpForm() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [alert, setalert] = useState({ visible: false, message: '', color: 'success' });
  const [loading, setloading] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(60);
  const [resendotp, setresendotp] = useState(false);

  const email = localStorage.getItem(RESET_EMAIL_KEY);

  const otpInputStyle = {
    width: '100%',
    height: '56px',
    textAlign: 'center',
    fontSize: '18px',
    borderRadius: '8px',
    boxSizing: 'border-box',
    padding: '8px',
    border: '1px solid #3F90DF',
    backgroundColor: '#151921',
    color: '#F3F4F6'
  };

  const otpContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  };

  const formatTime = (time) => (time < 10 ? `0${time}` : `${time}`);

  useEffect(() => {
    if (!email) {
      navigate('/forgot', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds > 0) {
          return prevSeconds - 1;
        }
        setMinutes((prevMinutes) => {
          if (prevMinutes > 0) {
            setSeconds(59);
            return prevMinutes - 1;
          }
          return 0;
        });
        return prevSeconds;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  async function verifyCode() {
    if (otp.length !== 6) {
      setalert({ visible: true, message: 'Enter the full 6-digit code.', color: 'error' });
      return;
    }

    setloading(true);
    setalert({ visible: false, message: '', color: 'success' });

    try {
      const result = await postJson(api.passwordVerifyOtp, {
        email,
        otp
      });

      sessionStorage.setItem(RESET_TOKEN_KEY, result.data?.resetToken || '');
      setalert({ visible: true, message: result.message, color: 'success' });
      setTimeout(() => {
        navigate('/changePassword', { replace: true });
      }, 400);
    } catch (error) {
      setalert({ visible: true, message: error.message, color: 'error' });
    } finally {
      setloading(false);
    }
  }

  async function resendCode() {
    if (!email) {
      navigate('/forgot', { replace: true });
      return;
    }

    setresendotp(true);
    setalert({ visible: false, message: '', color: 'success' });

    try {
      const result = await postJson(api.passwordForgot, { email });
      setMinutes(0);
      setSeconds(60);
      setalert({ visible: true, message: result.message, color: 'success' });
    } catch (error) {
      setalert({ visible: true, message: error.message, color: 'error' });
    } finally {
      setresendotp(false);
    }
  }

  const canResend = seconds === 0 && minutes === 0;

  return (
    <Stack spacing={2}>
      {alert.visible && (
        <Alert
          onClose={() => {
            setalert({ visible: false, message: '', color: 'success' });
          }}
          severity={alert.color}
        >
          {alert.message}
        </Alert>
      )}

      <div style={otpContainerStyle}>
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span>&nbsp;</span>}
          renderInput={(props) => <input {...props} style={otpInputStyle} inputMode="numeric" />}
        />
      </div>

      <LoadingButton
        onClick={verifyCode}
        fullWidth
        size="large"
        variant="contained"
        loading={loading}
        disabled={otp.length !== 6}
      >
        Continue
      </LoadingButton>

      <LoadingButton
        sx={{
          backgroundColor: canResend ? '#3F90DF' : '#ccc',
          color: canResend ? '#0B0F19' : '#6B7280',
          '&:hover': {
            backgroundColor: canResend ? '#2C6FB0' : '#ccc'
          }
        }}
        onClick={() => {
          if (canResend) {
            resendCode();
          }
        }}
        fullWidth
        size="large"
        variant="contained"
        loading={resendotp}
        disabled={!canResend}
      >
        {canResend ? 'Resend code' : `Resend in ${formatTime(minutes)}:${formatTime(seconds)}`}
      </LoadingButton>

      <Link component={RouterLink} to="/forgot" underline="hover" sx={{ color: '#9CA3AF', textAlign: 'center' }}>
        Use a different email
      </Link>
    </Stack>
  );
}
